import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

// Baji-style mobile bottom bar
// Menu • Casino • Deposit (floating CTA) • Slots • Profile
export const MobileBottomBar = ({ onMenuClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isActive = (to) => to && location.pathname.startsWith(to);

  const onDeposit = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    navigate("/profile/deposit");
  };

  const onProfile = () => {
    navigate(user ? "/dashboard" : "/login");
  };

  const profileActive =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/profile");

  return (
    <nav
      data-testid="mobile-bottom-bar"
      className="lg:hidden fixed bottom-0 left-0 right-0 z-30 h-[68px] border-t border-white/5"
      style={{
        background:
          "linear-gradient(180deg, rgba(10,17,25,0.98) 0%, rgba(10,17,25,1) 100%)",
        backdropFilter: "blur(12px)",
      }}
    >
      <div className="grid grid-cols-5 h-full items-stretch relative">
        <Tab
          testid="mbb-menu"
          label="Menu"
          active={false}
          onClick={onMenuClick}
          icon={<MenuIcon />}
        />
        <Tab
          testid="mbb-casino"
          label="Casino"
          to="/casino"
          active={isActive("/casino")}
          icon={<SpadeIcon />}
        />

        {/* Floating Deposit CTA */}
        <div className="relative flex items-start justify-center">
          <button
            type="button"
            onClick={onDeposit}
            data-testid="mbb-deposit"
            aria-label="deposit"
            className="absolute -top-4 h-[46px] w-[46px] rounded-full flex items-center justify-center active:scale-95 transition-transform"
            style={{
              background:
                "radial-gradient(120% 120% at 30% 25%, #2fd57a 0%, #19a55c 55%, #0a5a2f 100%)",
              boxShadow:
                "0 8px 18px rgba(25,165,92,0.55), inset 0 1px 0 rgba(255,255,255,0.4), 0 0 0 4px rgba(10,17,25,0.98)",
            }}
          >
            <PlusIcon />
          </button>
          <span className="absolute bottom-2 text-[11px] font-semibold text-[#21c46e] leading-none">
            Deposit
          </span>
        </div>

        <Tab
          testid="mbb-slots"
          label="Slots"
          to="/slots"
          active={isActive("/slots")}
          icon={<SlotsIcon />}
        />
        <Tab
          testid="mbb-profile"
          label="Profile"
          active={profileActive}
          onClick={onProfile}
          icon={<ProfileIcon />}
        />
      </div>
    </nav>
  );
};

const Tab = ({ testid, label, icon, active, onClick, to }) => {
  const inner = (
    <>
      {active && (
        <span
          aria-hidden
          className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-8 rounded-full"
          style={{
            background: "linear-gradient(90deg,#21c46e 0%,#d4a012 100%)",
            boxShadow: "0 0 8px rgba(33,196,110,0.7)",
          }}
        />
      )}
      <span className="h-7 w-7 flex items-center justify-center">{icon}</span>
      <span
        className={`text-[11px] font-semibold leading-none mt-1 ${
          active ? "text-[#21c46e]" : "text-white/70"
        }`}
      >
        {label}
      </span>
    </>
  );
  const cls =
    "relative flex flex-col items-center justify-center h-full w-full active:scale-[0.96] transition-transform";
  if (to) {
    return (
      <Link to={to} data-testid={testid} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={cls}>
      {inner}
    </button>
  );
};

// ---------------- Custom glossy SVG icons ----------------

const GreenGloss = ({ id }) => (
  <defs>
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#c8f5dd" />
      <stop offset="40%" stopColor="#21c46e" />
      <stop offset="100%" stopColor="#0a5a2f" />
    </linearGradient>
    <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="1.2" result="b" />
      <feMerge>
        <feMergeNode in="b" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
);

const GoldGloss = ({ id }) => (
  <defs>
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#ffe27a" />
      <stop offset="55%" stopColor="#d4a012" />
      <stop offset="100%" stopColor="#7a5a00" />
    </linearGradient>
  </defs>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden>
    <GreenGloss id="mg1" />
    <rect x="4" y="5" width="16" height="3.2" rx="1.2" fill="url(#mg1)" filter="url(#mg1-glow)" />
    <rect x="4" y="10.4" width="16" height="3.2" rx="1.2" fill="url(#mg1)" filter="url(#mg1-glow)" />
    <rect x="4" y="15.8" width="16" height="3.2" rx="1.2" fill="url(#mg1)" filter="url(#mg1-glow)" />
  </svg>
);

const SpadeIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden>
    <GreenGloss id="sp1" />
    <path
      d="M12 2.5c-2 2.8-7.5 6.4-7.5 10.8 0 2.6 2 4.6 4.6 4.6 1.3 0 2.4-.5 3.1-1.3-.2 1.4-.9 2.4-2.2 3.1v.9h3h3v-.9c-1.3-.7-2-1.7-2.2-3.1.7.8 1.8 1.3 3.1 1.3 2.6 0 4.6-2 4.6-4.6 0-4.4-5.5-8-7.5-10.8z"
      fill="url(#sp1)"
      filter="url(#sp1-glow)"
    />
    <path
      d="M12 4c-.5 1-1.4 1.9-2.5 3"
      stroke="rgba(255,255,255,0.55)"
      strokeWidth="1.2"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const SlotsIcon = () => (
  <svg viewBox="0 0 32 32" width="30" height="30" aria-hidden>
    <defs>
      <linearGradient id="slot-body" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#3be38a" />
        <stop offset="55%" stopColor="#19a55c" />
        <stop offset="100%" stopColor="#0a4a28" />
      </linearGradient>
      <linearGradient id="slot-gold" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffe891" />
        <stop offset="50%" stopColor="#e8b021" />
        <stop offset="100%" stopColor="#7a5400" />
      </linearGradient>
      <radialGradient id="slot-seven" cx="50%" cy="40%" r="65%">
        <stop offset="0%" stopColor="#ffd84d" />
        <stop offset="100%" stopColor="#c8841a" />
      </radialGradient>
      <filter id="slot-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="0.8" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* side lever arm */}
    <rect x="26.5" y="10" width="1.4" height="9" rx="0.7" fill="url(#slot-gold)" />
    {/* lever ball */}
    <circle cx="27.2" cy="9.2" r="2" fill="url(#slot-seven)" stroke="#7a4a00" strokeWidth="0.4" />
    <circle cx="26.6" cy="8.6" r="0.7" fill="rgba(255,255,255,0.7)" />

    {/* machine top arch */}
    <path
      d="M5 8 Q5 3 11 3 H21 Q27 3 27 8 V10 H5 Z"
      fill="url(#slot-body)"
      filter="url(#slot-glow)"
    />
    {/* marquee bulbs */}
    <circle cx="8.5" cy="6" r="0.7" fill="#ffe891" />
    <circle cx="12" cy="4.6" r="0.7" fill="#ffe891" />
    <circle cx="16" cy="4.2" r="0.7" fill="#ffe891" />
    <circle cx="20" cy="4.6" r="0.7" fill="#ffe891" />
    <circle cx="23.5" cy="6" r="0.7" fill="#ffe891" />

    {/* machine body */}
    <rect x="5" y="10" width="22" height="17" rx="2" fill="url(#slot-body)" filter="url(#slot-glow)" />

    {/* gold trim around screen */}
    <rect x="6.6" y="12" width="18.8" height="9.4" rx="1.2" fill="url(#slot-gold)" />
    {/* screen background */}
    <rect x="7.4" y="12.8" width="17.2" height="7.8" rx="0.8" fill="#0a1119" />

    {/* reel dividers */}
    <rect x="12.6" y="12.8" width="0.5" height="7.8" fill="rgba(255,255,255,0.15)" />
    <rect x="18.1" y="12.8" width="0.5" height="7.8" fill="rgba(255,255,255,0.15)" />

    {/* three big 7s */}
    <text x="10" y="19.2" fontFamily="Arial Black, Arial, sans-serif" fontSize="7" fontWeight="900" fill="url(#slot-seven)" textAnchor="middle">7</text>
    <text x="15.85" y="19.2" fontFamily="Arial Black, Arial, sans-serif" fontSize="7" fontWeight="900" fill="url(#slot-seven)" textAnchor="middle">7</text>
    <text x="21.7" y="19.2" fontFamily="Arial Black, Arial, sans-serif" fontSize="7" fontWeight="900" fill="url(#slot-seven)" textAnchor="middle">7</text>

    {/* JACKPOT bar under screen */}
    <rect x="8" y="22.4" width="16" height="2.2" rx="0.6" fill="url(#slot-gold)" />
    <text x="16" y="24.1" fontFamily="Arial, sans-serif" fontSize="1.9" fontWeight="900" fill="#3a1f00" textAnchor="middle" letterSpacing="0.3">JACKPOT</text>

    {/* coin tray */}
    <rect x="9" y="25.6" width="14" height="1.2" rx="0.5" fill="rgba(0,0,0,0.55)" />

    {/* base shadow */}
    <ellipse cx="16" cy="28.3" rx="11" ry="0.8" fill="rgba(0,0,0,0.4)" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
    <GoldGloss id="pl1" />
    <path
      d="M12 4v16 M4 12h16"
      stroke="url(#pl1)"
      strokeWidth="3.6"
      strokeLinecap="round"
    />
  </svg>
);

const ProfileIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden>
    <GreenGloss id="pr1" />
    <GoldGloss id="pr2" />
    <circle cx="12" cy="12" r="10" fill="url(#pr1)" filter="url(#pr1-glow)" />
    <circle cx="12" cy="10" r="3.2" fill="url(#pr2)" />
    <path
      d="M5.5 19c1.6-3 4-4.2 6.5-4.2S17 16 18.5 19"
      fill="url(#pr2)"
    />
  </svg>
);
